import {
  DISCOVER_ROUTE,
  getDiscoverLoginUrl,
} from "@circle-react/components/CommunitySwitcher/utils";
import { validateInviteMembersQueryParams } from "@circle-react/components/SettingsApp/ManageMembers/InviteMembers/queryParamsSchema";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useThemeContext } from "@circle-react/providers/Theme";
import { Drawer } from "@circleco/compass/components/Drawer";
import { Typography } from "@circleco/compass/components/Typography";
import { useNavigate } from "@tanstack/react-router";
import type { MouseEvent } from "react";

import { t } from "@/i18n-js/instance";
import { useCreateLoginToken } from "@/react/components/DiscoverComponent";
import { useCanSwitchCommunities } from "@/react/hooks/community_switcher/useCanSwitchCommunities";
import { useCommunitySwitcher } from "@/react/hooks/community_switcher/useCommunitySwitcherApi";

import { CommunityRow } from "./components/CommunityRow";
import { DrawerMenuItem } from "./components/DrawerMenuItem";

/**
 * Community switcher content rendered inside a nested child drawer,
 * triggered from the profile menu in MobileNavDrawerContent.
 *
 * Shows the current community (highlighted with a checkmark) followed
 * by a list of other communities the user belongs to. Tapping a
 * community navigates to it via automatic login URL.
 */
interface MobileCommunitySwitcherDrawerContentProps {
  /** Closes the parent community switcher drawer (controlled). */
  onClose?: () => void;
}

export const MobileCommunitySwitcherDrawerContent = ({
  onClose,
}: MobileCommunitySwitcherDrawerContentProps) => {
  const navigate = useNavigate();
  const { communities, token, isLoadingCommunities } = useCommunitySwitcher();
  const { isDarkAppearance } = useThemeContext();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  /** Close all drawers in the stack. */
  const closeAll = () => {
    onClose?.();
  };

  const currentCommunity = communities.find(c => c.is_current_community);
  const otherCommunities = communities.filter(c => !c.is_current_community);

  const canSwitchCommunities = useCanSwitchCommunities();

  // Matches the desktop community switcher (`useCommunitySwitcherAdapter`):
  // Discover no longer opens in-app via the `/discover` iframe route, it's a
  // full redirect straight to Discover's own domain, tagged with the same
  // `source=community_switcher` attribution param.
  const { mutate: generateDiscoverLoginToken } = useCreateLoginToken({
    onSuccess: data => {
      if (!data.login_url) return;
      globalThis.location.href = getDiscoverLoginUrl(
        data.login_url,
        currentCommunity?.id,
      );
    },
  });

  const onDiscoverClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    generateDiscoverLoginToken();
  };

  return (
    <>
      <Drawer.Header title={t("mobile_shell.communities")} sticky />
      {isLoadingCommunities ? (
        <div className="flex flex-col gap-4 px-1">
          <div className="flex items-center justify-center py-8">
            <Typography variant="body-sm" color="tertiary">
              {t("mobile_shell.loading")}
            </Typography>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 px-1">
          {/* Current community */}
          {currentCommunity && (
            <div className="rounded-lg bg-secondary">
              <CommunityRow
                community={currentCommunity}
                isDarkAppearance={Boolean(isDarkAppearance)}
                token={token}
                isCurrent
              />

              {isAdmin && (
                <div className="flex flex-col gap-0.5">
                  <DrawerMenuItem
                    icon="settings-gear"
                    label={t("settings_title")}
                    onClick={() => {
                      void navigate({ to: "/admin-settings" });
                      closeAll();
                    }}
                  />

                  <DrawerMenuItem
                    icon="people-add"
                    label={t("messaging.add_members")}
                    onClick={() => {
                      void navigate({
                        to: settingsNavPathV3.members.invite,
                        search: () => validateInviteMembersQueryParams({}),
                      });
                      closeAll();
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Other communities */}
          {canSwitchCommunities && otherCommunities.length > 0 && (
            <div className="flex flex-col">
              {otherCommunities.map(community => (
                <CommunityRow
                  key={community.id}
                  community={community}
                  isDarkAppearance={Boolean(isDarkAppearance)}
                  token={token}
                />
              ))}
            </div>
          )}

          {/* Empty state when user has no other communities */}
          {canSwitchCommunities &&
            currentCommunity &&
            otherCommunities.length === 0 && (
              <Typography variant="body-sm" color="tertiary">
                {t("mobile_shell.no_other_communities")}
              </Typography>
            )}

          {/* Discover */}
          {canSwitchCommunities && (
            <Drawer.Close nativeButton={false}>
              <a
                href={DISCOVER_ROUTE}
                onClick={onDiscoverClick}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-hover"
              >
                <span
                  aria-hidden
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg"
                >
                  <img
                    src="https://assets-v2.circle.so/11oepcqxo8q008li605zp2ol7a8a"
                    alt=""
                    width={32}
                    height={32}
                  />
                </span>

                <div className="flex min-w-0 flex-1 flex-col">
                  <Typography variant="body-md">
                    {t("mobile_shell.discover.title")}
                  </Typography>
                  <Typography variant="label-sm" color="tertiary">
                    {t("mobile_shell.discover.subtitle")}
                  </Typography>
                </div>
              </a>
            </Drawer.Close>
          )}
        </div>
      )}
    </>
  );
};
